import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { useAwmsStore } from '@/store/AwmsStore';
import { DataTable, FilterOption, Loader } from '@/components/Common';
import { useAcademicsData } from '@/hooks';
import { academicsColumns } from './AcademicsColumns';
import { FILTERS } from '@/constants';
import { Sheet } from '@/components/ui';
import { AcademicWorkload } from '../AcademicWorkload';
import { StaffUnit } from '../StaffUnit/StaffUnit';
import { Hdr } from '../Hdr/Hdr';
import { trpc } from '@/data-loaders';
import { generateFilterOptions } from '@/lib/utils';

export const Academics = () => {
  const selectedSchoolId = useAwmsStore((state) => state.selectedSchoolId);
  const setSelectedSchoolId = useAwmsStore((state) => state.setSelectedSchoolId);
  const selectedDisciplineId = useAwmsStore((state) => state.selectedDisciplineId);
  const setSelectedDisciplineId = useAwmsStore((state) => state.setSelectedDisciplineId);
  const selectedEmpStatusAbbr = useAwmsStore((state) => state.selectedEmpStatusAbbr);
  const setSelectedEmpStatusAbbr = useAwmsStore((state) => state.setSelectedEmpStatusAbbr);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const setActiveYear = useAwmsStore((state) => state.setActiveYear);
  const setSelectedStaff = useAwmsStore((state) => state.setSelectedStaff);
  const activeSheet = useAwmsStore((state) => state.activeSheet);
  const setActiveSheet = useAwmsStore((state) => state.setActiveSheet);
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const { academicsWorkloadAllocations, schoolList, disciplineList, empStatusAbbrList, isPageLoading } =
    useAcademicsData(selectedSchoolId, selectedDisciplineId, selectedEmpStatusAbbr, activeYear);
  const trpcUtils = trpc.useUtils();
  const years = useAwmsStore((state) => state.getYears);
  const [actionDropdownRowId, setActionDropdownRowId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [hasSavedAcademicWorkload, setHasSavedAcademicWorkload] = useState(false);
  const [hasSavedHdr, setHasSavedHdr] = useState(false);

  const startSnapshot = trpc.startSnapshot.useMutation();

  useEffect(() => {
    const yearOptions = years();
    if (!schoolList || !disciplineList || !empStatusAbbrList) return;

    const newFilters = [
      {
        placeholder: 'Select school',
        checkbox: false,
        options: generateFilterOptions(schoolList),
        stateFilterFieldName: FILTERS.SCHOOL,
        selectedState: selectedSchoolId,
        setSelectedState: setSelectedSchoolId,
      },
      {
        placeholder: 'Select discipline',
        checkbox: false,
        options: generateFilterOptions(disciplineList, 'withAll'),
        stateFilterFieldName: FILTERS.DISCIPLINE,
        selectedState: selectedDisciplineId,
        setSelectedState: setSelectedDisciplineId,
      },
      {
        placeholder: 'Select employment status',
        checkbox: true,
        options: generateFilterOptions(empStatusAbbrList),
        stateFilterFieldName: FILTERS.EMPSTATUSABBR,
        selectedState: selectedEmpStatusAbbr,
        setSelectedState: setSelectedEmpStatusAbbr,
      },
      {
        placeholder: 'Select year',
        checkbox: false,
        options: yearOptions.map((year) => {
          return {
            label: year,
            value: parseInt(year),
          };
        }),
        stateFilterFieldName: FILTERS.YEAR,
        selectedState: activeYear,
        setSelectedState: setActiveYear,
        className: 'w-[125px]',
      },
    ];

    // Only update state if newFilters is different from the current state
    if (!isEqual(filterOptions, newFilters)) {
      setFilterOptions(newFilters);
    }
  }, [
    schoolList,
    disciplineList,
    empStatusAbbrList,
    years,
    selectedDisciplineId,
    selectedSchoolId,
    selectedEmpStatusAbbr,
    activeYear,
    setSelectedSchoolId,
    setSelectedDisciplineId,
    setActiveYear,
    filterOptions,
  ]);

  if (isPageLoading) {
    return <Loader />;
  }

  return (
    <>
      <Sheet
        modal={activeSheet === 'staff' ? false : true}
        open={activeSheet !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActionDropdownRowId(null);
            setActiveSheet(null);
            if (hasSavedAcademicWorkload || hasSavedHdr) {
              trpcUtils.academic.getAcademicsWorkloadAllocations.invalidate({
                schoolId: selectedSchoolId,
                disciplineId: selectedDisciplineId,
              });
              trpcUtils.academic.getAcademicsWorkloadAllocations.refetch({
                schoolId: selectedSchoolId,
                disciplineId: selectedDisciplineId,
                year: activeYear,
              });
              setHasSavedAcademicWorkload(false);
              setHasSavedHdr(false);
            }
          }
        }}
      >
        {activeSheet === 'staff-workload' && (
          <AcademicWorkload setHasSavedAcademicWorkload={setHasSavedAcademicWorkload} />
        )}
        {activeSheet === 'staff' && (
          <StaffUnit
            schoolList={schoolList}
            actionDropdownRowId={actionDropdownRowId}
            setActionDropdownRowId={setActionDropdownRowId}
          />
        )}
        {activeSheet === 'hdr' && <Hdr setHasSavedHdr={setHasSavedHdr} />}

        <div className="max-h-[calc(100vh-60px-82px)] w-full overflow-y-auto">
          <DataTable
            id="academics-table"
            hasGlobalFilterHeader
            columns={academicsColumns({
              setActiveSheet,
              actionDropdownRowId,
              setActionDropdownRowId,
              setSelectedStaff,
              startSnapshot,
              activeYear,
            })}
            data={academicsWorkloadAllocations?.length ? academicsWorkloadAllocations : []}
            tableProps={{ padding: 'px-8' }}
            globalFilterHeaderProps={{
              searchColumn: { searchPlaceHolder: 'Search staff by id or name', columnId: 'academicName' },
              filterOptions: filterOptions,
            }}
            headerProps={{ primaryColor: 'bg-deep-blue-50', secondaryColor: 'bg-banana-50' }}
            dividerColumnProps={{ dividerColumnId: 'teaching', borderStyling: 'border-r-[3px] border-deep-blue' }}
            isPageLoading={isPageLoading}
            pagination={pagination}
            setPagination={setPagination}
            defaultSort={{ id: 'academicName' }}
          />
        </div>
      </Sheet>
    </>
  );
};
